import { PropsWithChildren, useState } from "react";

import { useNotification } from "@refinedev/core";
import { InstagramIcon } from "lucide-react";

import { PhotoUpload } from "@/components/fields/photo-upload";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useArtistCreateForm } from "@/pages/artists/create/hooks/use-artist-create-form";

interface AiArtistEnrichmentStepProps {}

const AiArtistEnrichmentStep =
  ({}: PropsWithChildren<AiArtistEnrichmentStepProps>) => {
    const [instagramScreenshot, setInstagramScreenshot] = useState("");
    const {
      onSpotifyEnrichmentSubmit,
      onAiEnrichmentStepSubmit,
      basicInfoStep,
      spotifyEnrichmentStep,
    } = useArtistCreateForm();

    const { open } = useNotification();

    const instagramUrl = spotifyEnrichmentStep?.socialLinks?.find(
      (link) => link.type.toLowerCase() === "instagram",
    )?.url;

    return (
      <div>
        <div className="flex flex-col space-y-2">
          {basicInfoStep && (
            <Card>
              <CardHeader>
                <CardTitle>Artist Information</CardTitle>
                <CardDescription>
                  Current artist data to be enriched with AI-powered insights
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  <div>
                    <p className="text-sm font-medium">Name</p>
                    <p className="text-sm text-muted-foreground">
                      {basicInfoStep.name}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Bio</p>
                    <p className="text-sm text-muted-foreground">
                      {spotifyEnrichmentStep?.bio}
                    </p>
                  </div>
                  {instagramUrl && (
                    <div>
                      <p className="text-sm font-medium">Instagram URL</p>
                      <Button
                        onClick={() => window.open(instagramUrl, "_blank")}
                        variant="default"
                        size="sm"
                      >
                        <InstagramIcon size={16} className="mr-2" />
                        Go to Instagram
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          <Label htmlFor="#instagramPageUrlInput">Instagram Screenshot</Label>
          <PhotoUpload
            value={instagramScreenshot}
            onChange={(value) => {
              if (!value) {
                open?.({
                  type: "error",
                  message: "Error processing uploaded photo",
                });
                return;
              }
              setInstagramScreenshot(value);
            }}
            transformToWebP
          />
        </div>
        <div className="flex justify-end">
          <Button
            onClick={() => {
              onAiEnrichmentStepSubmit({ instagramScreenshot });
            }}
            size="lg"
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

export default AiArtistEnrichmentStep;
